import { FunctionComponent } from 'react';
import { useFormContext } from 'react-hook-form';
import type { CreateKidDocumentValues } from '../CreateKidDocumentModal';
import type { KidLayoutStyleSummary } from '../../../../../../api/kids/types';

interface NewDocumentFlowProps {
  creationMethod: CreateKidDocumentValues['creationMethod'];
  layoutStyle?: string;
  isLoadingLayoutStyles: boolean;
  kidLayoutStyles?: KidLayoutStyleSummary[];
  onChangeCreationMethod: (value: 'blank' | 'recommended') => void;
  onSelectTemplateStyle: (id: string, name: string) => void;
}

const NewDocumentFlow: FunctionComponent<NewDocumentFlowProps> = ({
  creationMethod,
  layoutStyle,
  isLoadingLayoutStyles,
  kidLayoutStyles,
  onChangeCreationMethod,
  onSelectTemplateStyle,
}) => {
  // Access form context just to ensure we are within a FormProvider,
  // even though we don't need the values directly here.
  useFormContext<CreateKidDocumentValues>();

  return (
    <div className="space-y-4 rounded-md border border-dashed border-gray-300 p-4">
      <div>
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="radio"
              name="new-layout"
              checked={creationMethod === 'blank'}
              onChange={() => onChangeCreationMethod('blank')}
            />
            <span className="font-medium">Blank document</span>
            <span className="text-gray-500">
              – start with an empty document and build from scratch
            </span>
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="radio"
              name="new-layout"
              checked={creationMethod === 'recommended'}
              onChange={() => onChangeCreationMethod('recommended')}
            />
            <span className="font-medium">Recommended layout</span>
            <span className="text-gray-500">
              – use our optimized structure and best practices
            </span>
          </label>
        </div>
      </div>

      {/* Tertiary: Layout style selection (only for recommended layout) */}
      {creationMethod === 'recommended' && (
        <div className="space-y-4">
          <div className="rounded-md border border-gray-300 bg-white p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-gray-700">Layout Styles</p>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              Select a pre-configured layout style with content and formatting
            </p>
            {isLoadingLayoutStyles ? (
              <div className="text-xs text-gray-500">Loading layout styles…</div>
            ) : kidLayoutStyles && kidLayoutStyles.length > 0 ? (
              <div className="grid grid-cols-1 gap-2">
                {kidLayoutStyles.map((layout) => (
                  <label
                    key={layout.kid_layout_id}
                    className={
                      'flex cursor-pointer items-start gap-2 rounded-md border capitalize p-2 hover:bg-blue-50 ' +
                      (layoutStyle === layout.kid_layout_id
                        ? 'border-mainblue bg-blue-50'
                        : '')
                    }
                  >
                    <input
                      type="radio"
                      name="layout-style"
                      value={layout.kid_layout_id}
                      checked={layoutStyle === layout.kid_layout_id}

                      onChange={() =>
                        onSelectTemplateStyle(
                          layout.kid_layout_id,
                          layout.kid_layout_name,
                        )
                      }
                    />
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-gray-800">
                        {layout.kid_layout_name}
                      </div>
                      <div className="text-xs text-gray-600">
                        {layout.kid_layout_level} layout
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="rounded-md bg-gray-50 p-3 text-xs text-gray-500">
                None available.
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default NewDocumentFlow;

